import { CircleExclamationFilledIcon } from '@/src/components/icon/filled/CircleExclamationFilledIcon';
import { CircleInformationFilledIcon } from '@/src/components/icon/filled/CircleInformationFilledIcon';
import { WarningFilledIcon } from '@/src/components/icon/filled/WarningFilledIcon';
import { Box, BoxProps, SvgIconProps, Typography, TypographyProps } from '@mui/material';
import React, { ComponentType, ElementType, forwardRef, useMemo } from 'react';

export interface FormMessageProps extends BoxProps<ElementType> {
  /**
   * Defines which variant of Banner component should be rendered
   * @default error
   */
  variant?: 'info' | 'warning' | 'error';
  /**
   * Properties passed to indicator icon
   */
  iconProps?: SvgIconProps;
  /**
   * Icon node to be used
   */
  IconComponent?: ComponentType<SvgIconProps>;
  /**
   * Properties passed to wrapper of children
   */
  typographyProps?: TypographyProps<ElementType>;
}

/**
 * Message indicating form error or additional info.
 * Intended to be used in combination with <FormGroup />
 */
export const FormMessage: React.FC<FormMessageProps> = forwardRef(
  ({ children, variant = 'error', sx, iconProps, IconComponent, typographyProps, ...rest }, ref) => {
    const { sx: iconSx, ...restIcon } = iconProps || {};
    const { sx: typographySx, ...restTypography } = typographyProps || {};

    const [color, IconVariantComponent] = useMemo(() => {
      switch (variant) {
        case 'error':
          return ['error.main', WarningFilledIcon];
        case 'warning':
          return ['warning.main', CircleExclamationFilledIcon];
        case 'info':
        default:
          return ['info.main', CircleInformationFilledIcon];
      }
    }, [variant]);
    const ErrorMessageIcon = IconComponent || IconVariantComponent;

    return (
      <Box
        role="alert"
        aria-atomic="true"
        aria-live="assertive"
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1,
          textAlign: 'left',
          color: color,
          pb: 1,
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        <ErrorMessageIcon
          sx={{
            fontSize: '20px',
            // align with checkbox/radio (return to 24px width)
            mx: '2px',
            ...iconSx,
          }}
          {...restIcon}
        />
        <Typography
          sx={{
            flexGrow: '1',
            // custom font according to Figma
            fontSize: { xs: '14px' },
            lineHeight: { xs: '20px' },
            fontWeight: { xs: '400' },
            ...typographySx,
          }}
          {...restTypography}
        >
          {children}
        </Typography>
      </Box>
    );
  },
);
